import omit from 'lodash/omit'
import convertDecimalPlaces from 'core/common/utils/convertDecimalPlaces'
import { isString, isBoolean } from 'core/common/utils/is'

export const WEIGHT_UNITS = {
  kg: 'kg',
  g: 'g',
}

export const SIZE_UNITS = {
  cm: 'cm',
  m: 'm',
}

export const parseNumber = value => {
  if (isString(value)) {
    return Number(value.replace(/\D+/g, ''))
  }
  return value || 0
}

export const parseDimensions = productPackage => {
  const parsedDimensions = {
    height: convertDecimalPlaces(productPackage.height),
    width: convertDecimalPlaces(productPackage.width),
    depth: convertDecimalPlaces(productPackage.depth),
    weight: convertDecimalPlaces(productPackage.weight.value || productPackage.weight),
  }

  if (!productPackage.standard_packing) {
    if (productPackage.unit === SIZE_UNITS.m) {
      parsedDimensions.height = convertDecimalPlaces(parsedDimensions.height * 100)
      parsedDimensions.width = convertDecimalPlaces(parsedDimensions.width * 100)
      parsedDimensions.depth = convertDecimalPlaces(parsedDimensions.depth * 100)
    }
  }

  if (productPackage.weight.unit === WEIGHT_UNITS.kg) {
    const decimalProductPackage = productPackage.weight.value || productPackage.weight
    parsedDimensions.weight = convertDecimalPlaces(decimalProductPackage * 1000)
  }

  return parsedDimensions
}

export const generateStockAndPriceFromChannels = (channels, initialValues = {}) => {
  const prices = channels.map(channel => ({
    price: initialValues.price ?? 0,
    list_price: initialValues.list_price ?? 0,
    channel: channel.id,
  }))

  const stocks = channels.map(channel => ({
    quantity: 0,
    type: 'on_seller',
    channel: channel.id,
  }))

  return {
    prices,
    stocks,
  }
}

export const generateStockOnSeller = (stockArray, channels = []) => {
  const stock = stockArray?.map(item => ({ ...item, quantity: item.quantity ?? 0 })) ?? []

  if (!stock.length || !stock.some(item => item.type === 'on_seller')) {
    const generatedStock = channels.map(channel => ({
      quantity: 0,
      type: 'on_seller',
      channel,
    }))

    return [...generatedStock, ...stock]
  }

  const on_seller = stock.filter(item => item.type === 'on_seller')

  return [...on_seller]
}

export const parseMatchVariation = (values, variation) => ({
  attributes: variation.attributes,
  images: variation.images,
  fulfillment: variation.fulfillment ?? false,
  stocks: variation.stocks,
  prices: variation.prices,
  sku: variation.sku,
  ean: variation.ean || values.ean,
  active: variation.active,
})

export const parseVariation = variation => {
  let parsedVariation = {
    attributes: variation.customAttributes?.map(attribute => omit(attribute, ['id'])),
    images: variation.images?.filter(image => image.url).map(image => image.url),
    fulfillment: variation.fulfillment ?? false,
    stocks: variation.stocks,
    prices: variation.prices,
    sku: variation.sku,
    ean: variation.ean,
    has_ean: !variation.not_has_ean,
    active: variation.active,
  }

  if (variation.package) {
    const newPackageValue = parseDimensions({ ...variation.package })

    parsedVariation = {
      ...parsedVariation,
      package: newPackageValue,
      dimension: newPackageValue,
    }
  }

  if (isBoolean(variation.fulfillment)) {
    parsedVariation = {
      ...parsedVariation,
      fulfillment: variation.fulfillment,
    }
  }

  return parsedVariation
}

export const changeValueOfKeys = (object, allowed) => {
  const filteredObject = object
  allowed.forEach(key => {
    if (!object[key]) {
      filteredObject[key] = null
    }
  })
  return filteredObject
}

export const convertPackageValues = (selectedFreight, selectedPackage, unit) => {
  switch (unit) {
    case SIZE_UNITS.cm:
      return {
        width: convertDecimalPlaces(selectedFreight.width / 10),
        height: convertDecimalPlaces(selectedFreight.height / 10),
        depth: convertDecimalPlaces(selectedFreight.depth / 10),
        size: selectedPackage,
      }
    case SIZE_UNITS.m:
      return {
        width: convertDecimalPlaces(selectedFreight.width / 1000),
        height: convertDecimalPlaces(selectedFreight.height / 1000),
        depth: convertDecimalPlaces(selectedFreight.depth / 1000),
        size: selectedPackage,
      }
    default:
      throw new Error('Tipo de unidade não reconhecida')
  }
}

export const convertProductToVariation = (product, { selectedAttributes, type }) => {
  const variation = {
    active: product.active,
    customAttributes: Object.entries(selectedAttributes)
      .filter(
        ([, customAttribute]) => customAttribute.description && customAttribute.value.length > 0
      )
      .map(([id, customAttribute]) => {
        const attribute = product.attributes.find(
          findableAttribute =>
            findableAttribute.type.toLowerCase() === customAttribute.description.toLowerCase()
        )

        return {
          id: `${id}-${attribute?.value.toLowerCase()}`,
          ...attribute,
        }
      }),
    fulfillment: product.fulfillment,
    images: product.images.map(image => ({ url: image })),
    prices: product.prices,
    promotion_count: product.promotion_count,
    promotional_prices: product.promotional_prices,
    stocks: product.stocks,
    sku: product.sku,
    id: product.id,
    status: product.status,
    ean: product.ean || '',
    not_has_ean: isBoolean(product.has_ean) ? !product.has_ean : undefined,
    type,
  }

  return variation
}

export const hydrateMatchVariation = (variation, channels) => {
  return {
    ...variation,
    id: variation.attributes.map(attribute => attribute.value).join('-'),
    sku: '',
    selected: false,
    ...generateStockAndPriceFromChannels(channels),
  }
}

export const toLowerStringValues = objArray => {
  return objArray.map(obj =>
    Object.entries(obj).reduce(
      (lowerStringedObject, [key, value]) => ({
        ...lowerStringedObject,
        [key]: value.toLowerCase(),
      }),
      {}
    )
  )
}

export const calculateErrorQuantity = errors => {
  const datasheetErrors = (errors.datasheet || []).reduce(
    (errorQty, error) => errorQty + Object.keys(error || {}).length,
    0
  )

  const variationAttributeErrors = Object.entries(errors.selectedAttributes || {}).reduce(
    (errorQty, [, field]) => errorQty + Object.entries(field).length,
    0
  )
  const variationErrors = (errors.variations?.filter(variation => variation) || []).reduce(
    (errorQty, variation) => errorQty + Object.entries(variation).length,
    0
  )
  const priceErrors = Object.entries(errors.prices || {}).reduce(
    (errorQty, [, field]) => errorQty + Object.entries(field).length,
    0
  )

  const stockErrors = Object.entries(errors.stocks || {}).reduce(
    (errorQty, [, field]) => errorQty + Object.entries(field).length,
    0
  )

  const matchVariationErrors = (errors.matchVariations || []).reduce(
    (errorQty, variation) => errorQty + Object.entries(variation).length,
    0
  )

  const filteredErrors = omit(errors, [
    'datasheet',
    'selectedAttributes',
    'variations',
    'prices',
    'stocks',
    'matchVariations',
  ])
  const otherErrors = Object.entries(filteredErrors).length

  return (
    datasheetErrors +
    variationAttributeErrors +
    variationErrors +
    otherErrors +
    matchVariationErrors +
    priceErrors +
    stockErrors
  )
}

export const extractSKUErrors = err => {
  return err.details.filter(detail => !Object.keys(detail).sku).map(detail => detail.sku)
}

const groupAttributes = group => {
  // get all attributes from the group
  const allAttributes = group.reduce((attributes, groupProduct) => {
    return [...attributes, ...groupProduct.attributes.map(attribute => attribute)]
  }, [])

  // group the attributes by the type
  const groupedAttributes = allAttributes.reduce((groupedAttributesAcc, attribute) => {
    if (groupedAttributesAcc[attribute.type]) {
      return {
        ...groupedAttributesAcc,
        [attribute.type]: [...new Set([...groupedAttributesAcc[attribute.type], attribute.value])],
      }
    }
    return {
      ...groupedAttributesAcc,
      [attribute.type]: [attribute.value],
    }
  }, {})

  return groupedAttributes
}

const generateSelectedAttributes = group => {
  const groupedAttributes = groupAttributes(group)

  // parse to formik
  return Object.entries(groupedAttributes).reduce(
    (selectedAttributesAcc, [selectedAttributeType, selectedAttributeValues], index) => {
      return {
        ...selectedAttributesAcc,
        [`id${index}`]: {
          description: selectedAttributeType,
          value: selectedAttributeValues,
        },
      }
    },
    {}
  )
}

export const hydrateImages = (images, { enableCover } = { enableCover: true }) => {
  return images.map((image, index) => ({
    id: image,
    id_ref: image,
    file: null,
    src: null,
    url: image,
    cover: enableCover && index === 0,
    percentage: 100,
    uploaded: true,
    uploadStatus: 'done',
    selected: false,
    error: false,
  }))
}

export const countPromotions = product =>
  (product.promotion_count ?? 0) + (product.promotional_prices?.length ?? 0)

export const convertAtributes = group => {
  const groupedAttributes = groupAttributes(group)

  const findAtt = (type, arr) =>
    arr.findIndex(
      findableAttribute => findableAttribute.type.toLowerCase() === type.toLowerCase()
    ) !== -1

  group.forEach(({ attributes }) => {
    Object.keys(groupedAttributes).forEach(item => {
      if (!findAtt(item, attributes)) {
        attributes.push({
          type: item,
          value: groupedAttributes[item][0],
        })
      }
    })
  })
}

export const extractVariations = (product, group) => {
  const isVariationEnabled = !!product?.attributes?.length
  const selectedAttributes = generateSelectedAttributes(group)

  convertAtributes(group)

  const variations = group.map(groupProduct => ({
    ...convertProductToVariation(groupProduct, {
      channels: product.channels,
      selectedAttributes,
      type: 'existingProduct',
    }),
    images: hydrateImages([...new Set(groupProduct.images)]),
  }))

  if (isVariationEnabled) {
    return {
      isVariationEnabled,
      selectedAttributes,
      variations,
    }
  }

  return {}
}
